// SignInContent.tsx

import "./SignInContent.scss";
import { useEffect, useMemo, useRef, useState } from "react";
import { CircularLoader } from "../../../CircularLoadingIndicator/CircularLoadingIndicator";
import {
    // getIsUserVerified2,
    MiniKit,
    // VerificationLevel,
    // type VerifyCommandInput,
} from "@worldcoin/minikit-js";
import type { MiniAppWalletAuthSuccessPayload } from "@worldcoin/minikit-js/commands";
import toast from "react-hot-toast";
import { NS, NS2 } from "../../../../../state/config";
import { useTranslation } from "react-i18next";
import { WorldAuthClient } from "@bridge-applications/world-auth";
import { z } from "zod";
import {
    ApiAuthCompleteSiweReqSchema,
    ApiAuthCompleteSiweResSchema,
} from "@bridge-applications/eh-types";
import { useSlice } from "@bridge-applications/provider-kit";
import { claim } from "../../../../../claim";
import WrongEnvironmentPopup, {
    type WrongEnvironmentAdminPayload,
} from "../../../WrongEnvironmentPopup/WrongEnvironmentPopup";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowRight,
    faGift,
} from "@awesome.me/kit-f3e20eab48/icons/classic/solid";

function isValidWalletAddress(addr: string | null): addr is string {
    return typeof addr === "string" && /^0x[a-fA-F0-9]{40}$/.test(addr);
}

interface SignInContentProps {
    onSuccess: () => void;
}

interface IpData {
    ip?: string;
    network?: string;
    version?: string;
    city?: string;
    region?: string;
    region_code?: string;
    country?: string;
    country_name?: string;
    country_code?: string;
    country_code_iso3?: string;
    country_capital?: string;
    country_tld?: string;
    continent_code?: string;
    in_eu?: boolean;
    postal?: string;
    latitude?: number;
    longitude?: number;
    timezone?: string;
    utc_offset?: string;
    country_calling_code?: string;
    currency?: string;
    currency_name?: string;
    languages?: string;
    country_area?: number;
    country_population?: number;
    asn?: string;
    org?: string;
}

export default function SignInContent({ onSuccess }: SignInContentProps) {
    const [signInStatus, setSignInStatus] = useState<
        | "idle"
        | "loading"
        | "success"
        | "error"
        | "error-referral-invalid-code"
        | "error-referral-campaign-not-live"
        | "error-referral-existing-account"
    >("idle");
    const { t } = useTranslation();
    const { setSlice } = useSlice("appSlice");

    // ---- Onboarding flag in Local Storage ----
    // Namespace-aligned key to record if the user has already completed onboarding at least once.
    const HAS_ONBOARDED_KEY = `${NS2}hasOnboarded`;
    const [hasOnboarded, setHasOnboarded] = useState<boolean>(false);

    const [currentUrl, setCurrentUrl] = useState<string>(window.location.href);
    const [referral, setReferral] = useState<string>(""); // 5 chars, [A-Z0-9]
    const lastErrorWasServerReferral = useRef<
        | "error-referral-invalid-code"
        | "error-referral-campaign-not-live"
        | undefined
    >(undefined);
    const ERROR_CLEAR_MS = 1500;
    const REFERRAL_CAMPAIGN_ID = "e4d95fd7-d95b-48bd-9347-af5c9621848f";
    const [wrongEnvOpen, setWrongEnvOpen] = useState(false);

    // Normalize to 5 chars uppercase alphanumeric only
    function normalizeReferral(input: string): string {
        return input
            .replace(/[^a-z0-9]/gi, "")
            .toUpperCase()
            .slice(0, 5);
    }

    // Valid if exactly 5 chars A-Z0-9
    const isReferralValid = useMemo(
        () => /^[A-Z0-9]{5}$/.test(referral),
        [referral],
    );
    const hasReferralChars = referral.length > 0;
    const disableForInvalidReferral = hasReferralChars && !isReferralValid;

    useEffect(() => {
        setCurrentUrl(window.location.href);
    }, [window.location.href]);

    // Read the onboarding flag on mount. If not present or not "true", treat as false.
    useEffect(() => {
        try {
            const v = localStorage.getItem(HAS_ONBOARDED_KEY);
            setHasOnboarded(v === "true");
        } catch {
            // do nothing
        }
    }, []);

    // Prefill from the legacy ?r= param only. RefV2 links are handled by app-session.
    useEffect(() => {
        try {
            const url = new URL(window.location.href);
            const r = url.searchParams.get("r");
            if (r) setReferral(normalizeReferral(r));
        } catch {
            // do nothing
        }
    }, []);

    // If the server told us the code/campaign is invalid or not-live,
    // clear the error a short time after the user edits the input.
    useEffect(() => {
        if (
            signInStatus === "error-referral-invalid-code" ||
            signInStatus === "error-referral-campaign-not-live"
        ) {
            lastErrorWasServerReferral.current = signInStatus;
        }
        if (
            lastErrorWasServerReferral.current &&
            (signInStatus === "error-referral-invalid-code" ||
                signInStatus === "error-referral-campaign-not-live")
        ) {
            // still displaying server error; if user changes text, schedule clear
            const timer = setTimeout(() => {
                // Only clear if user actually changed the field (i.e., not identical)
                setSignInStatus("idle");
                lastErrorWasServerReferral.current = undefined;
            }, ERROR_CLEAR_MS);
            return () => clearTimeout(timer);
        }
    }, [referral, signInStatus]);

    async function signInAsAdmin(payload: WrongEnvironmentAdminPayload) {
        if (disableForInvalidReferral || signInStatus === "loading") return;

        let resetToIdle = true;
        setSignInStatus("loading");

        try {
            const { nonce } = await WorldAuthClient.getNonce();

            const { adminApiKey, walletProfile, worldUsername } = payload;

            localStorage.setItem(`${NS}walletAddress`, walletProfile);
            localStorage.setItem(`${NS2}worldUsername`, worldUsername);
            setSlice({ username: worldUsername });

            let data: IpData | undefined;
            try {
                const _data = await fetch("https://ipapi.co/json/");
                data = await _data.json();
            } catch {
                // do nothing
            }

            const bodyBase: z.infer<typeof ApiAuthCompleteSiweReqSchema> = {
                notisEnabled: true,
                worldUsername,
                client: {
                    ip: data?.ip ?? undefined,
                    ipVersion: data?.version ?? undefined,
                    estCity: data?.city ?? undefined,
                    estCountryName: data?.country_name ?? undefined,
                    estCountryCode: data?.country_code ?? undefined,
                    estRegion: data?.region ?? undefined,
                    estRegionCode: data?.region_code ?? undefined,
                    estCoords: {
                        lat: data?.latitude ?? undefined,
                        lng: data?.longitude ?? undefined,
                    },
                    estTimezone: data?.timezone ?? undefined,
                    network: data?.network ?? undefined,
                    org: data?.org ?? undefined,
                    langs: data?.languages ?? undefined,
                },
                nonce,
                walletProfile,
            };

            const body: z.infer<typeof ApiAuthCompleteSiweReqSchema> =
                isReferralValid
                    ? {
                          ...bodyBase,
                          inviteCode: referral,
                          referralCampaignId: REFERRAL_CAMPAIGN_ID,
                      }
                    : bodyBase;

            const res = (await WorldAuthClient.completeSiwe(
                body,
                undefined,
                undefined,
                { "x-admin-api-key": adminApiKey },
                { deferCommit: true },
            )) as unknown as z.infer<typeof ApiAuthCompleteSiweResSchema>;

            // Handle referral statuses from server
            const rc = res.inviteCodeStatus;
            if (hasReferralChars) {
                if (rc === "INVALID_CODE" || rc === "CAMPAIGN_NOT_FOUND") {
                    resetToIdle = false;
                    setSignInStatus("error-referral-invalid-code");
                    return;
                }
                if (rc === "CAMPAIGN_NOT_LIVE") {
                    resetToIdle = false;
                    setSignInStatus("error-referral-campaign-not-live");
                    return;
                }
                if (rc === "ALREADY_HAS_ACCOUNT") {
                    toast(() => (
                        <span>
                            {t("SignInContent.referralExistingAccount")}
                        </span>
                    ));
                }
                if (
                    rc === "APPLIED" &&
                    Array.isArray(res.referralVouchers) &&
                    res.referralVouchers.length > 0
                ) {
                    try {
                        const txs = res.referralVouchers.map((v) => ({
                            address: v.domain.verifyingContract,
                            abi: [
                                {
                                    inputs: [
                                        {
                                            components: [
                                                { name: "to", type: "address" },
                                                {
                                                    name: "tokenType",
                                                    type: "uint256",
                                                },
                                                {
                                                    name: "token",
                                                    type: "address",
                                                },
                                                {
                                                    name: "amount",
                                                    type: "uint256",
                                                },
                                                {
                                                    name: "tokenId",
                                                    type: "uint256",
                                                },
                                                {
                                                    name: "expiresAt",
                                                    type: "uint256",
                                                },
                                                {
                                                    name: "nonce",
                                                    type: "uint256",
                                                },
                                            ],
                                            name: "v",
                                            type: "tuple",
                                        },
                                        { name: "signature", type: "bytes" },
                                    ],
                                    name: "claim",
                                    outputs: [],
                                    stateMutability: "nonpayable",
                                    type: "function",
                                },
                            ],
                            functionName: "claim",
                            args: [v.struct, v.signature],
                        }));
                        await claim(txs);
                        toast.success(t("SignInContent.referralClaimed"));
                    } catch (err) {
                        console.error("voucher claim failed", err);
                        toast.error(t("SignInContent.referralClaimFailed"));
                    }
                }
            }

            localStorage.setItem(`${NS2}walletAddress`, walletProfile);
            setSlice({ walletAddress: walletProfile });

            if (res.status !== "OK") {
                if (
                    res.inviteCodeStatus === "INVALID_CODE" ||
                    res.inviteCodeStatus === "CAMPAIGN_NOT_FOUND"
                ) {
                    resetToIdle = false;
                    setSignInStatus("error-referral-invalid-code");
                    return;
                }
                if (res.inviteCodeStatus === "CAMPAIGN_NOT_LIVE") {
                    resetToIdle = false;
                    setSignInStatus("error-referral-campaign-not-live");
                    return;
                }
                toast.error(
                    res.message ?? t("SignInContent.failedToConnectWallet"),
                );
                setSignInStatus("error");
                return;
            }

            WorldAuthClient.commitAuth({
                accessToken: res.accessToken,
                accessTokenExpiresAt: res.accessTokenExpiresAt,
                refreshTokenExpiresAt: res.refreshTokenExpiresAt,
            });

            try {
                localStorage.setItem(HAS_ONBOARDED_KEY, "true");
                setHasOnboarded(true);
            } catch {
                // do nothing
            }

            // IMPORTANT: do NOT store adminApiKey anywhere (no LS, no env)
            onSuccess();
        } catch (err) {
            console.error(err);
            setSignInStatus("error");
        } finally {
            if (resetToIdle) setSignInStatus("idle");
        }
    }

    async function connectWallet() {
        if (disableForInvalidReferral || signInStatus === "loading") return;

        // If MiniKit isn't installed, show the unsupported env popup instead of using .env secrets.
        if (!MiniKit.isInstalled()) {
            setWrongEnvOpen(true);
            return;
        }

        let resetToIdle = true;
        setSignInStatus("loading");
        let walletAddress: string = "";

        try {
            const { nonce } = await WorldAuthClient.getNonce();
            console.log("nonce", nonce);

            let finalPayload: MiniAppWalletAuthSuccessPayload;

            if (MiniKit.isInstalled()) {
                /* ----------------------------- REAL-WORLD-ENV ----------------------------- */
                console.log("[eh-client] world-env");

                /* ------------------------------- WALLET-AUTH ------------------------------ */
                const { data } = await MiniKit.walletAuth({
                    nonce,
                    expirationTime: new Date(
                        Date.now() + 7 * 24 * 60 * 60 * 1000,
                    ),
                    notBefore: new Date(Date.now() - 24 * 60 * 60 * 1000),
                    statement: t("SignInContent.signInToContinue"),
                });
                finalPayload = {
                    ...data,
                    status: "success",
                    version: data.version ?? 1,
                };

                if (
                    finalPayload.status === "success" &&
                    isValidWalletAddress(finalPayload.address)
                ) {
                    console.log("finalPayload", finalPayload);
                    localStorage.setItem(
                        `${NS}walletAddress`,
                        finalPayload.address,
                    );

                    // MiniKit v2's walletAuth() no longer populates
                    // MiniKit.user.username / profilePictureUrl (that only
                    // happened via the legacy subscribe wrapper in v1), so
                    // fetch the profile explicitly from the usernames API.
                    let worldUsername: string | undefined;
                    let worldProfilePictureUrl: string | undefined;
                    try {
                        const userInfo = await MiniKit.getUserByAddress(
                            finalPayload.address,
                        );
                        worldUsername = userInfo.username ?? undefined;
                        worldProfilePictureUrl =
                            userInfo.profilePictureUrl ?? undefined;
                    } catch {
                        // Username is optional for sign-in; don't block auth.
                    }

                    if (worldUsername) {
                        localStorage.setItem(
                            `${NS2}worldUsername`,
                            worldUsername,
                        );
                        setSlice({
                            username: worldUsername,
                        });
                    }

                    if (worldProfilePictureUrl) {
                        localStorage.setItem(
                            `${NS2}worldProfilePictureUrl`,
                            worldProfilePictureUrl,
                        );
                    }

                    /* --------------------- TODO: IMPLEMENT FOLLOWING INFO --------------------- */
                    console.log(
                        "MiniKit.deviceProperties.deviceOS",
                        MiniKit.deviceProperties.deviceOS,
                    );
                    console.log(
                        "MiniKit.deviceProperties.worldAppVersion",
                        MiniKit.deviceProperties.worldAppVersion,
                    );
                    console.log(
                        "MiniKit.deviceProperties.safeAreaInsets",
                        MiniKit.deviceProperties.safeAreaInsets,
                    );

                    walletAddress = finalPayload.address;
                    console.log("walletAddress", walletAddress);

                    // IP

                    let data: IpData | undefined;

                    try {
                        const _data = await fetch("https://ipapi.co/json/");
                        // const data: IpData = await _data.json();
                        data = await _data.json();
                    } catch {
                        // do nothing
                    }

                    // const _data = await fetch("https://ipapi.co/json/");
                    // data = await _data.json();

                    const bodyBase = {
                        notisEnabled: true,
                        worldUsername,
                        client: {
                            ip: data?.ip ?? undefined,
                            ipVersion: data?.version ?? undefined,
                            estCity: data?.city ?? undefined,
                            estCountryName: data?.country_name ?? undefined,
                            estCountryCode: data?.country_code ?? undefined,
                            estRegion: data?.region ?? undefined,
                            estRegionCode: data?.region_code ?? undefined,
                            estCoords: {
                                lat: data?.latitude ?? undefined,
                                lng: data?.longitude ?? undefined,
                            },
                            estTimezone: data?.timezone ?? undefined,
                            network: data?.network ?? undefined,
                            org: data?.org ?? undefined,
                            langs: data?.languages ?? undefined,
                        },
                        payload: finalPayload as typeof finalPayload & {
                            status: "success";
                        },
                        nonce,
                    };

                    const body = isReferralValid
                        ? {
                              ...bodyBase,
                              inviteCode: referral,
                              referralCampaignId: REFERRAL_CAMPAIGN_ID,
                          }
                        : bodyBase;

                    const res = (await WorldAuthClient.completeSiwe(
                        body,
                        undefined,
                        undefined,
                        {},
                        { deferCommit: true },
                    )) as unknown as z.infer<
                        typeof ApiAuthCompleteSiweResSchema
                    >;

                    // Handle referral statuses from server
                    const rc = res.inviteCodeStatus;
                    if (hasReferralChars) {
                        if (
                            rc === "INVALID_CODE" ||
                            rc === "CAMPAIGN_NOT_FOUND"
                        ) {
                            resetToIdle = false;
                            setSignInStatus("error-referral-invalid-code");
                            return;
                        }
                        if (rc === "CAMPAIGN_NOT_LIVE") {
                            resetToIdle = false;
                            setSignInStatus("error-referral-campaign-not-live");
                            return;
                        }
                        if (rc === "ALREADY_HAS_ACCOUNT") {
                            // Sign in proceeds; inform the user they can't redeem
                            toast(() => (
                                <span>
                                    {t("SignInContent.referralExistingAccount")}
                                </span>
                            ));
                            // continue
                        }
                        if (
                            rc === "APPLIED" &&
                            Array.isArray(res.referralVouchers) &&
                            res.referralVouchers.length > 0
                        ) {
                            // Claim vouchers before completing sign-in
                            try {
                                const txs = res.referralVouchers.map((v) => ({
                                    address: v.domain.verifyingContract,
                                    // Minimal ABI: claim((address,uint256,address,uint256,uint256,uint256,uint256),bytes)
                                    abi: [
                                        {
                                            inputs: [
                                                {
                                                    components: [
                                                        {
                                                            name: "to",
                                                            type: "address",
                                                        },
                                                        {
                                                            name: "tokenType",
                                                            type: "uint256",
                                                        },
                                                        {
                                                            name: "token",
                                                            type: "address",
                                                        },
                                                        {
                                                            name: "amount",
                                                            type: "uint256",
                                                        },
                                                        {
                                                            name: "tokenId",
                                                            type: "uint256",
                                                        },
                                                        {
                                                            name: "expiresAt",
                                                            type: "uint256",
                                                        },
                                                        {
                                                            name: "nonce",
                                                            type: "uint256",
                                                        },
                                                    ],
                                                    name: "v",
                                                    type: "tuple",
                                                },
                                                {
                                                    name: "signature",
                                                    type: "bytes",
                                                },
                                            ],
                                            name: "claim",
                                            outputs: [],
                                            stateMutability: "nonpayable",
                                            type: "function",
                                        },
                                    ],
                                    functionName: "claim",
                                    args: [v.struct, v.signature],
                                }));
                                await claim(txs);
                                toast.success(
                                    t("SignInContent.referralClaimed"),
                                );
                            } catch (err) {
                                console.error("voucher claim failed", err);
                                toast.error(
                                    t("SignInContent.referralClaimFailed"),
                                );
                                // proceed with sign-in anyway
                            }
                        }
                    }

                    localStorage.setItem(
                        `${NS2}walletAddress`,
                        finalPayload.address,
                    );
                    setSlice({
                        walletAddress: finalPayload.address,
                    });

                    // No referral-code errors → commit auth + finish
                    // WorldAuthClient.commitAuth({
                    //     accessToken: res.accessToken,
                    //     accessTokenExpiresAt: res.accessTokenExpiresAt,
                    //     refreshTokenExpiresAt: res.refreshTokenExpiresAt,
                    // });
                    // // Only proceed when there are no referral-code *errors*
                    // // (ALREADY_HAS_ACCOUNT is informational and allowed to proceed)
                    // onSuccess();

                    // Final guard: only proceed when the server actually returned OK
                    if (res.status !== "OK") {
                        // Map server-side referral errors to your UI, else generic error
                        if (
                            res.inviteCodeStatus === "INVALID_CODE" ||
                            res.inviteCodeStatus === "CAMPAIGN_NOT_FOUND"
                        ) {
                            resetToIdle = false;
                            setSignInStatus("error-referral-invalid-code");
                            return;
                        }
                        if (res.inviteCodeStatus === "CAMPAIGN_NOT_LIVE") {
                            resetToIdle = false;
                            setSignInStatus("error-referral-campaign-not-live");
                            return;
                        }
                        toast.error(
                            res.message ??
                                t("SignInContent.failedToConnectWallet"),
                        );
                        setSignInStatus("error");
                        return;
                    }

                    WorldAuthClient.commitAuth({
                        accessToken: res.accessToken,
                        accessTokenExpiresAt: res.accessTokenExpiresAt,
                        refreshTokenExpiresAt: res.refreshTokenExpiresAt,
                    });
                    // Mark that onboarding has been completed at least once.
                    try {
                        localStorage.setItem(HAS_ONBOARDED_KEY, "true");
                        setHasOnboarded(true);
                    } catch {
                        // do nothing
                    }
                    onSuccess();
                } else {
                    console.log("finalPayload", finalPayload);
                    toast.error(t("SignInContent.failedToConnectWallet"));
                }
            }
        } catch (err) {
            console.error(err);
            setSignInStatus("error");
        } finally {
            if (resetToIdle) setSignInStatus("idle");
        }
    }

    // If the user has already onboarded, render ONLY the primary sign-in button
    // and nothing else (no referral UI, debug text, etc.).
    if (hasOnboarded === true) {
        return (
            <div className={`sign-in-content ${signInStatus}`}>
                <button
                    className={`sign-in-content__btn ${
                        signInStatus === "loading" ? "loading" : ""
                    }`}
                    onClick={connectWallet}
                    disabled={
                        signInStatus === "loading" || disableForInvalidReferral
                    }
                    data-disabled={
                        signInStatus === "loading" ||
                        disableForInvalidReferral ||
                        undefined
                    }
                    aria-disabled={
                        signInStatus === "loading" ||
                        disableForInvalidReferral ||
                        undefined
                    }
                    title={
                        disableForInvalidReferral
                            ? "Enter a 5-character code (A–Z, 0–9)"
                            : undefined
                    }
                >
                    <h3>{t("SignInContent.signIn")}</h3>
                    {signInStatus === "loading" ? (
                        <div className="sign-in-content__btn__loader">
                            <CircularLoader color="#fff" thickness={3} />
                        </div>
                    ) : (
                        // <FontAwesomeIcon icon={FontAwesomeSolid.faArrowRight} />
                        // <FontAwesomeSolid icon={faArrowRight} />
                        <FontAwesomeIcon icon={faArrowRight} />
                    )}
                </button>

                <WrongEnvironmentPopup
                    open={wrongEnvOpen}
                    busy={signInStatus === "loading"}
                    onClose={() => setWrongEnvOpen(false)}
                    onSubmitAdmin={async (p) => {
                        setWrongEnvOpen(false);
                        await signInAsAdmin(p);
                    }}
                    initialWalletProfile={localStorage.getItem(
                        `${NS2}walletAddress`,
                    )}
                    initialWorldUsername={localStorage.getItem(
                        `${NS2}worldUsername`,
                    )}
                />
            </div>
        );
    }

    return (
        <div className={`sign-in-content ${signInStatus}`}>
            {/* DEBUGGING */}
            {currentUrl.includes("?r=") && <p>{currentUrl}</p>}{" "}
            <div className="sign-in-content__referral">
                <p className="sign-in-content__referral__title">
                    {t("SignInContent.inviteCodeOptional")}
                </p>

                <div className="sign-in-content__referral__input-wrapper">
                    <FontAwesomeIcon icon={faGift} />
                    <input
                        type="text"
                        inputMode="text"
                        autoComplete="one-time-code"
                        aria-label="Invite code"
                        aria-invalid={
                            signInStatus === "error-referral-invalid-code" ||
                            signInStatus === "error-referral-campaign-not-live"
                                ? true
                                : undefined
                        }
                        maxLength={5}
                        value={referral}
                        placeholder="ABCDE"
                        onChange={(e) => {
                            const next = normalizeReferral(e.target.value);
                            setReferral(next);
                        }}
                    />
                </div>

                {signInStatus === "error-referral-invalid-code" && (
                    <p className="sign-in-content__referral__error">
                        {/* This code is invalid. Please try again. */}
                        {t("SignInContent.invalidCode")}
                    </p>
                )}
                {signInStatus === "error-referral-campaign-not-live" && (
                    <p className="sign-in-content__referral__error">
                        {/* This campaign is not live. Remove the code to continue. */}
                        {t("SignInContent.campaignNotLive")}
                    </p>
                )}
                {signInStatus === "error-referral-existing-account" && (
                    <p className="sign-in-content__referral__error">
                        {/* Code only valid for new users! Remove the code to continue. */}
                        {t("SignInContent.referralExistingAccount")}
                    </p>
                )}
            </div>
            <div className="hdiv" />
            <button
                className={`sign-in-content__btn ${
                    signInStatus === "loading" ? "loading" : ""
                }`}
                onClick={connectWallet}
                disabled={
                    signInStatus === "loading" || disableForInvalidReferral
                }
                data-disabled={
                    signInStatus === "loading" ||
                    disableForInvalidReferral ||
                    undefined
                }
                aria-disabled={
                    signInStatus === "loading" ||
                    disableForInvalidReferral ||
                    undefined
                }
                title={
                    disableForInvalidReferral
                        ? "Enter a 5-character code (A–Z, 0–9)"
                        : undefined
                }
            >
                <h3>{t("SignInContent.signIn")}</h3>

                {signInStatus === "loading" ? (
                    <div className="sign-in-content__btn__loader">
                        <CircularLoader color="#fff" thickness={3} />
                    </div>
                ) : (
                    <FontAwesomeIcon icon={faArrowRight} />
                )}
            </button>
            <WrongEnvironmentPopup
                open={wrongEnvOpen}
                busy={signInStatus === "loading"}
                onClose={() => setWrongEnvOpen(false)}
                onSubmitAdmin={async (p) => {
                    setWrongEnvOpen(false);
                    await signInAsAdmin(p);
                }}
                initialWalletProfile={localStorage.getItem(
                    `${NS2}walletAddress`,
                )}
                initialWorldUsername={localStorage.getItem(
                    `${NS2}worldUsername`,
                )}
            />
        </div>
    );
}
